import type { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
} from '@sola/ark-core/molecules/dialog/dialog.js';
import { Form, useFormMethods } from '@sola/ark-core/molecules/form/form.js';
import {
  RadioGroup,
  RadioItemProps,
} from '@sola/ark-core/molecules/radio-group/radio-group.js';
import { getPaymentDay } from '@sola/my-sdk/utils.js';
import { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.js';
import { paymentMethodsQuery } from '@sola/supporter-sdk/queries.js';

import { getPaymentMethodLabel } from '../../atoms/payment-method-icon/payment-method-icon';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts';
import { LocalAlertConfig } from '../../types/localeAlerts';
import { PaymentMethodAddSheet } from '../payment-method-add-sheet/payment-method-add-sheet';

const ADD_NEW_PAYMENT_METHOD_VALUE = 'add-new';

export type PaymentMethodChoiceItem = {
  id: GraphQLSupporterPaymentMethod['id'];
  label?: string;
  nickname?: GraphQLSupporterPaymentMethod['accountNickname'];
};

export const useComponentData = (t: TFunction) => {
  // TODO: Also tap into the isLoading, isPending state for when we change the payment method for this commitment so that we can disable the button during this process.
  const { data, isFetching, isLoading, isPending, isError } =
    useSupporterQuery(paymentMethodsQuery);

  const paymentMethodItems: PaymentMethodChoiceItem[] = Array.isArray(data)
    ? (data as GraphQLSupporterPaymentMethod[]).map(
        (pm: GraphQLSupporterPaymentMethod) => ({
          id: pm.id,
          label: getPaymentMethodLabel(pm, t),
          nickname: pm.accountNickname,
        }),
      )
    : [];

  // TODO: Is this also used for the delete choice? We don't set the paymentDay for that scenario
  const today = new Date().getDate();
  const paymentDay = getPaymentDay(today);

  return {
    isShowingSkeletons: isLoading || isPending || isFetching,
    isError,
    paymentMethodItems,
    paymentDay,
  };
};

export const PaymentMethodChoiceDialog: React.FC<{
  open: boolean;
  isBusy?: boolean;
  currentPaymentMethod?: GraphQLSupporterPaymentMethod;
  currentPaymentDay?: number | null;
  onOpenChange: (open: boolean) => void;
  handlePaymentMethodSelection: (
    paymentMethodId: GraphQLSupporterPaymentMethod['id'],
    paymentDay?: number,
  ) => Promise<void>;
  dialogAlerts?: LocalAlertConfig[];
  allowAddPaymentMethod?: boolean;
}> = ({
  open = false,
  isBusy = false,
  currentPaymentMethod,
  currentPaymentDay,
  onOpenChange,
  handlePaymentMethodSelection,
  dialogAlerts,
  allowAddPaymentMethod = false,
}) => {
  const { t } = useTranslation(['my', 'core']);
  const { isShowingSkeletons, isError, paymentMethodItems, paymentDay } =
    useComponentData(t);
  const paymentPullDay = currentPaymentDay || paymentDay;
  const formMethods = useFormMethods();
  const { removeAlert: removeSheetAlert } = useModalAlerts();

  const radioItems: RadioItemProps[] = useMemo(
    () => [
      ...paymentMethodItems.map((pm) => ({
        value: pm.id || '',
        element: (
          <span className="flex items-center gap-comfy-xs">
            {pm.label}
            {pm.nickname ? <Badge label={pm.nickname} size="sm" /> : null}
          </span>
        ),
      })),
      ...(allowAddPaymentMethod
        ? [
            {
              value: ADD_NEW_PAYMENT_METHOD_VALUE,
              text: t('commitments.notRecurring.choosePaymentMethod.addNew'),
            },
          ]
        : []),
    ],
    [allowAddPaymentMethod, paymentMethodItems.length],
  );

  // TODO: Doesn't seem like we should need a local state - should use the formMethods...
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    | GraphQLSupporterPaymentMethod['id']
    | typeof ADD_NEW_PAYMENT_METHOD_VALUE
    | undefined
  >(currentPaymentMethod?.id);

  const handleSubmit = (data: FieldValues) => {
    const { paymentMethodId } = data;
    handlePaymentMethodSelection(
      paymentMethodId as GraphQLSupporterPaymentMethod['id'],
      paymentPullDay,
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      onClose={() => setSelectedPaymentMethod(undefined)}
    >
      <DialogContent
        size="md"
        icon="info-circle"
        purpose="neutral"
        title={t('commitments.notRecurring.choosePaymentMethod.title')}
        supportingText={t(
          'commitments.notRecurring.choosePaymentMethod.supportingText',
        )}
      >
        <Form
          onSubmit={handleSubmit}
          formMethods={formMethods}
          aria-label={t('commitments.notRecurring.choosePaymentMethod.title')}
        >
          {isShowingSkeletons && <Skeleton height={100} width={250} />}
          {!isShowingSkeletons && isError && (
            <Alert
              variant="negative"
              title={t('commitments.details.paymentMethod.queryError.title')}
              message={t(
                'commitments.details.paymentMethod.queryError.message',
              )}
              action={
                <a href={t('ctas.contactSupport.url')}>
                  {t('ctas.contactSupport.text')}
                </a>
              }
            />
          )}
          {dialogAlerts?.length ? (
            <div>
              {dialogAlerts.map((alert) => (
                <LocalAlert
                  key={alert.id}
                  alert={alert}
                  className="mb-comfy-sm"
                  onClose={removeSheetAlert}
                />
              ))}
            </div>
          ) : undefined}
          {/* TODO: Likely need a scenario for when allowAddPaymentMethod is false and there are no payment methods... */}
          {!isShowingSkeletons && !isError && Array.isArray(radioItems) && (
            <>
              <RadioGroup
                name="paymentMethodId"
                // TODO: Don't think we're supposed to use the defaultValue prop - and instead should use formMethods
                defaultValue={selectedPaymentMethod}
                variant="default"
                onChange={(v: React.ChangeEvent<HTMLInputElement>) => {
                  setSelectedPaymentMethod(v.target.value);
                }}
                items={radioItems as RadioItemProps[]}
                required
              />

              {paymentPullDay && (
                <>
                  <Divider purpose="neutral" className="my-comfy-xl" />

                  <div className="label-lg label-neutral-mild pb-comfy-sm">
                    <span className="label-potent">
                      {t('commitments.details.paymentDay.title')}
                      {': '}
                    </span>

                    {t('commitments.details.paymentDay.paymentPullDayLabel', {
                      count: paymentPullDay,
                      ordinal: true,
                    })}
                  </div>
                </>
              )}

              <DialogActions>
                <DialogActionClose>
                  {t('core:actions.cancel')}
                </DialogActionClose>
                {selectedPaymentMethod === ADD_NEW_PAYMENT_METHOD_VALUE ? (
                  <PaymentMethodAddSheet
                    // TODO: When successfully adding a payment method, we want to automatically select it and call handlePaymentMethodSelection with the new payment method id. We can achieve this by passing a callback to the onComplete prop of the PaymentMethodAddSheet that receives the new payment method as an argument.
                    onComplete={(newPaymentMethod) => {
                      handlePaymentMethodSelection(
                        newPaymentMethod.id,
                        paymentPullDay,
                      );
                    }}
                    openTrigger={
                      <DialogActionPrimary>
                        {t('core:actions.continue')}
                      </DialogActionPrimary>
                    }
                  />
                ) : (
                  <DialogActionPrimary
                    disabled={selectedPaymentMethod === undefined}
                    isBusy={isBusy}
                    type="submit"
                    // TODO: why doesn't disableTillValid work?
                    // disableUntilFilled
                  >
                    {t('core:actions.save')}
                  </DialogActionPrimary>
                )}
              </DialogActions>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  );
};
